import clsx from 'clsx'
import type { DraftBlockType } from 'lib/community/discussions-writer'
import styles from './Writer.module.css'

interface PhoneToolbarProps {
  /** The keyboard's height (`useViewportInsets`): the bar rides on it. */
  inset: number
  canAdd: boolean
  canReorder: boolean
  reordering: boolean
  /** Why Post is off (`postDisabledReason`), or null. */
  reason: string | null
  onAdd: (type: Exclude<DraftBlockType, 'text'>) => void
  onReorder: () => void
}

const ADD: { type: Exclude<DraftBlockType, 'text'>; glyph: string; label: string }[] = [
  { type: 'heading', glyph: 'H', label: 'Add a heading' },
  { type: 'images', glyph: '▦', label: 'Add photos' },
  { type: 'link', glyph: '🔗', label: 'Add a link' },
]

// A press on the bar must not take focus from the field being typed in: the keyboard would close
// and the bar would drop under the finger. mousedown only — a cancelled pointerdown drops the
// tap's click in WebKit. The click still fires.
const keepFocus = (e: React.MouseEvent): void => e.preventDefault()

/**
 * The phone writer's toolbar (design M1c): Heading, Photos, Link, Reorder — 44px targets — and,
 * on the right, why Post is off. Fixed to the screen's bottom (safe area included), lifted onto
 * the on-screen keyboard while one is up. Text is the default block: no button for it. Hidden
 * above 767.98px.
 */
const PhoneToolbar: React.VFC<PhoneToolbarProps> = ({
  inset,
  canAdd,
  canReorder,
  reordering,
  reason,
  onAdd,
  onReorder,
}) => (
  <div
    className={clsx(styles.toolbar, { [styles.toolbarLifted]: inset > 0 })}
    style={inset > 0 ? { transform: `translateY(${-inset}px)` } : undefined}
    role="toolbar"
    aria-label="Add to your post"
  >
    {ADD.map(button => (
      <button
        key={button.type}
        type="button"
        className={styles.toolbarButton}
        aria-label={button.label}
        title={button.label}
        disabled={!canAdd || reordering}
        onMouseDown={keepFocus}
        onClick={() => onAdd(button.type)}
      >
        <span aria-hidden="true">{button.glyph}</span>
      </button>
    ))}
    <button
      type="button"
      className={clsx(styles.toolbarButton, { [styles.toolbarOn]: reordering })}
      aria-label="Reorder blocks"
      aria-pressed={reordering}
      title="Reorder blocks"
      disabled={!canReorder && !reordering}
      onMouseDown={keepFocus}
      onClick={onReorder}
    >
      <span aria-hidden="true">{'⇅'}</span>
    </button>
    <span className={styles.toolbarReason}>{reason}</span>
  </div>
)

export default PhoneToolbar
